<template>
  <div class="contact-page-wrapper">

    <PageHeader
        :title="(contactData && contactData.page_title)"
        :subtitle="(contactData && contactData.page_subtitle) || ''"
    />

    <div class="contact-page-inner">

      <div
          v-if="formStatus.message"
          :class="['contact-form-alert', formStatus.isSuccess ? '--success' : '--danger']"
      >
        <span class="alert-icon">{{ formStatus.isSuccess ? '✓' : '⚠' }}</span>
        {{ formStatus.message }}
      </div>

      <div class="contact-grid-row">

        <div
            class="contact-info-col contact-sidebar"
            v-if="contactData.contact_header || contactData.contact_header_two"
        >
          <div class="contact-sidebar__block" v-if="contactData.contact_header">
            <h3 class="contact-sidebar__title">{{ contactData.contact_header }}</h3>
            <ul class="contact-sidebar__list" v-if="parsedListOne.length">
              <li v-for="(item, index) in parsedListOne" :key="index">
                <span class="bullet">→</span> {{ item }}
              </li>
            </ul>
          </div>

          <div class="contact-sidebar__block margin-top" v-if="contactData.contact_header_two">
            <h3 class="contact-sidebar__title">{{ contactData.contact_header_two }}</h3>
            <ul class="contact-sidebar__list" v-if="parsedListTwo.length">
              <li v-for="(item, index) in parsedListTwo" :key="index">
                <span class="bullet">→</span> {{ item }}
              </li>
            </ul>
          </div>
        </div>

        <div class="contact-form-col">
          <div class="contact-form-header">
            <h2 class="contact-form-title">Submit A Question</h2>
            <p class="contact-form-subtext">Please send us a ticket by filling out the validation fields below.</p>
          </div>

          <form @submit.prevent="handleSubmit" class="contact-tech-form">
            <div class="form-group">
              <label class="form-label">Full Name *</label>
              <input type="text" v-model="formFields.full_name" placeholder="e.g. John Doe" required class="form-input" />
            </div>

            <div class="form-group">
              <label class="form-label">Email Address *</label>
              <input type="email" v-model="formFields.email" placeholder="name@company.com" required class="form-input" />
            </div>

            <div class="form-group">
              <label class="form-label">Phone Number *</label>
              <input type="text" v-model="formFields.phone_number" placeholder="07771122333" required class="form-input" />
            </div>

            <div class="form-group">
              <label class="form-label">Subject *</label>
              <input type="text" v-model="formFields.subject" placeholder="Project Scope or Network Inquiry" required class="form-input" />
            </div>

            <div class="form-group">
              <label class="form-label">Message Details *</label>
              <textarea v-model="formFields.message" rows="5" placeholder="Describe your architectural requirements..." required class="form-textarea"></textarea>
            </div>

            <div class="form-group turnstile-wrapper">
              <div
                  ref="turnstileRef"
                  id="captcha-container"
                  class="cf-turnstile"
                  :data-sitekey="turnstileSiteKey"
              >
              </div>
            </div>

            <div class="contact-form-footer">
              <button type="submit" class="contact-submit-btn" :disabled="isSubmitting">
                <span v-if="!isSubmitting">Submit Ticket</span>
                <span v-else>Processing Request...</span>
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageHeader from "../components/PageHeader.vue";

export default defineComponent({
  name: 'ContactPage',
  components: {PageHeader},
  props: {
    contactData: {
      type: Object,
      required: true
    }
  },
  setup(props) {

    console.log(props.contactData);

    const turnstileRef = ref(null);

    const turnstileSiteKey = globalThis.wpSettings?.turnstileSiteKey || '';

    const formFields = ref({
      full_name: props.contactData.full_name || '',
      email: props.contactData.email || '',
      subject: props.contactData.subject || '',
      message: props.contactData.message || '',
      phone_number: props.contactData.phone_number || ''
    });

    const isSubmitting = ref(false);

    const formStatus = ref({
      message: '',
      isSuccess: true
    });

    const parsedListOne = computed(() => {
      if (!props.contactData.contact_text) return [];
      return props.contactData.contact_text.split('\n').map(item => item.trim()).filter(Boolean);
    });

    const parsedListTwo = computed(() => {
      if (!props.contactData.contact_list_for_header_two) return [];
      return props.contactData.contact_list_for_header_two.split('\n').map(item => item.trim()).filter(Boolean);
    });

    let widgetId = null;

    onMounted(() => {
      widgetId = window.turnstile.render(turnstileRef.value, {
        sitekey: turnstileSiteKey
      });
    });

    const handleSubmit = async () => {
      isSubmitting.value = true;
      formStatus.value.message = '';

      const tokenElement = document.querySelector('input[name="cf-turnstile-response"]');
      const captchaToken = tokenElement ? tokenElement.value : '';

      if (!captchaToken) {
        formStatus.value.isSuccess = false;
        formStatus.value.message = 'Please complete the security verification check.';
        isSubmitting.value = false;
        return;
      }

      try {
        const payload = new contactData();
        payload.append('action', 'secure_tech_contact');
        payload.append('full_name', formFields.value.full_name);
        payload.append('email', formFields.value.email);
        payload.append('phone_number', formFields.value.phone_number);
        payload.append('subject', formFields.value.subject);
        payload.append('message', formFields.value.message);
        payload.append('g-recaptcha-response', captchaToken);

        const response = await fetch(
            window.ajaxurl || '/wp-admin/admin-ajax.php',
            {
              method: 'POST',
              body: payload
            }
        );

        const data = await response.json();

        if (data.success) {
          formStatus.value.isSuccess = true;
          formStatus.value.message = data.data?.message || 'Message sent successfully.';

          // 1. Clear form fields
          formFields.value.full_name = '';
          formFields.value.email = '';
          formFields.value.phone_number = '';
          formFields.value.subject = '';
          formFields.value.message = '';

          // 2. Reset Turnstile using the widget ID
          // widgetId.value is the reference we stored during onMounted
          if (widgetId.value !== null && window.turnstile) {
            window.turnstile.reset(widgetId.value);
            console.log('Turnstile widget reset successfully.');
          }
        } else {
          formStatus.value.isSuccess = false;
          formStatus.value.message = data.data?.message || 'Unable to send your message.';

          // Optional: Reset on error too, in case the token expired while typing
          if (widgetId.value !== null && window.turnstile) {
            window.turnstile.reset(widgetId.value);
          }
        }
      } catch (error) {
        console.error('Contact form error:', error);

        formStatus.value.isSuccess = false;
        formStatus.value.message =
            'An unexpected error occurred. Please try again later.';
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      formFields,
      isSubmitting,
      formStatus,
      parsedListOne,
      parsedListTwo,
      turnstileSiteKey,
      handleSubmit,
      turnstileRef
    };
  }
});
</script>
